import type { Rng } from '../../core/rng/Rng';
import {
  ABILITY_DEFS, ABILITY_IDS, ABILITY_SLOTS, makeAbility,
  type AbilityId, type AbilityInstance,
} from '../abilities/AbilityDefs';
import type { CharacterId } from '../characters/CharacterDefs';

export interface SkillChoice {
  defId: AbilityId;
  /** false = a NEW skill; true = the one behavioral upgrade to an owned skill. */
  isUpgrade: boolean;
}

export interface OfferContext {
  /** 0-based pick index (RunState.picksConsumed at roll time). */
  pickIndex?: number;
  /** Thematic weighting table key. */
  character?: CharacterId;
  /** firstWin milestone: forgiveness runs through pick index 2, not 1. */
  guidedPicks?: boolean;
  /** Skills banished this run — never offered again (new OR upgrade cards). */
  banished?: ReadonlySet<AbilityId>;
}

/**
 * Upgrade cards for owned skills weigh heavier than new skills — leaning
 * into what you have should be the LIKELY offer, not the lucky one.
 */
export const UPGRADE_WEIGHT = 2.5;

/** Banishes per run: strike a card's skill from all future offers. Free. */
export const BANISHES_PER_RUN = 3;

/**
 * Rerolls are unlimited but gem-priced, escalating per use (run-wide):
 * 25, 40, 64, 102, 164… — paid from the wallet (gemsHeld), never touching
 * gemsEarned or the unbanked stake.
 */
export function rerollCost(rerollsUsed: number): number {
  return Math.round(25 * 1.6 ** rerollsUsed);
}

/**
 * Per-character offer weighting: bias toward the character's thematic space
 * WITHOUT locking anyone out of anything (absent = weight 1; never 0).
 */
export const CHARACTER_OFFER_WEIGHTS:
  Partial<Record<CharacterId, Partial<Record<AbilityId, number>>>> = {
  wick: { caustic: 3, rupture: 2, rime: 0.5 },
  mourne: { rupture: 3, splinter: 3, caustic: 2, brand: 2 },
  vane: { ricochet: 2, splinter: 2 },
  barrow: { undertow: 3, recoil: 3, fracture: 2 },
  trine: { ricochet: 2, swarm: 2, fracture: 2 },
  bramGaunt: { tithe: 2, swarm: 2, undertow: 2 },
  cask: { rupture: 3, splinter: 2 },
  thirl: { undertow: 3, rime: 2, recoil: 2 },
};

/**
 * Pick 1-of-3: new skills (while a slot among 2–6 is open) ∪ the single
 * upgrade for each owned-but-unupgraded skill — INCLUDING the signature.
 * Signatures are never offered as NEW. Rolled from the per-floor `levelups`
 * stream so offers are seed-fair.
 *
 * Determinism contract: the stream draw count is a fixed function of the
 * candidate pool (exactly one draw per candidate — weighted-shuffle keys),
 * never a rejection loop.
 *
 * Early forgiveness (the doc's catch-up substitute, replacing rerolls): for
 * pick index 0–1 (0–2 with guidedPicks) NEW emitters weigh ×2.5 and
 * rule-changer/loop cards ×0.4 — a rule-changer with nothing to multiply is
 * the classic whiffed first pick.
 */
export function rollOffers(
  owned: AbilityInstance[],
  rng: Rng,
  ctx: OfferContext = {},
): SkillChoice[] {
  const pool: SkillChoice[] = [];
  const ownedIds = new Set(owned.map((a) => a.defId));
  const banished = ctx.banished;

  for (const inst of owned) {
    if (!inst.upgraded && !banished?.has(inst.defId)) {
      pool.push({ defId: inst.defId, isUpgrade: true });
    }
  }
  if (owned.length < ABILITY_SLOTS) {
    for (const id of ABILITY_IDS) {
      if (ABILITY_DEFS[id].signature) continue;
      if (banished?.has(id)) continue;
      if (!ownedIds.has(id)) pool.push({ defId: id, isUpgrade: false });
    }
  }

  const charWeights = ctx.character !== undefined
    ? CHARACTER_OFFER_WEIGHTS[ctx.character]
    : undefined;
  const forgivingUntil = ctx.guidedPicks ? 3 : 2;
  const forgiving = (ctx.pickIndex ?? Number.MAX_SAFE_INTEGER) < forgivingUntil;

  // Weighted shuffle (exponential-key trick): one draw per candidate, sort by
  // key^(1/weight) descending — equivalent to sampling without replacement.
  const keyed = pool.map((choice) => {
    const def = ABILITY_DEFS[choice.defId];
    let weight = charWeights?.[choice.defId] ?? 1;
    if (choice.isUpgrade) weight *= UPGRADE_WEIGHT;
    if (forgiving && !choice.isUpgrade) {
      if (def.tag === 'emitter') weight *= 2.5;
      else if (def.tag === 'ruleChanger' || def.tag === 'loop') weight *= 0.4;
    }
    const u = Math.max(1e-9, rng.next());
    return { choice, key: Math.pow(u, 1 / Math.max(1e-6, weight)) };
  });
  keyed.sort((a, b) => b.key - a.key);
  return keyed.slice(0, 3).map((k) => k.choice);
}

export function applyOffer(owned: AbilityInstance[], choice: SkillChoice): void {
  if (choice.isUpgrade) {
    const inst = owned.find((a) => a.defId === choice.defId);
    if (inst) inst.upgraded = true;
  } else if (owned.length < ABILITY_SLOTS) {
    owned.push(makeAbility(choice.defId));
  }
}
